import type { EndpointResponse, Olympus } from '@owner/olympus-client'
import { storeToRefs } from 'pinia'
import {
  computed,
  onBeforeMount,
  onBeforeUnmount,
  ref,
  toRaw,
  watch,
  type ComputedRef,
  type Ref,
} from 'vue'

import { useAuthStore } from '@/auth/useAuthStore'
import { getActiveIframeSession } from '@/auth/iframeSession'
import { useOwnerAgentStore } from '@/features/agent/composables/useOwnerAgentStore'
import { openUpsellSheet } from '@/features/upsell/sheetState'
import {
  UpsellSource,
  type UpsellSourceValue,
} from '@/features/upsell/upsellTracking'
import { VITE_OLYMPUS_URL } from '@/OlympusClient'

import {
  buildParentToHestiaEvent,
  isHestiaModalScrimSyncMessage,
  isHestiaToParentRelayRequest,
  isHestiaUpsellOpenRequestMessage,
  normalizeEmbedToHestiaIframeTarget,
} from './hestiaIframeEvents'
import {
  postMessageToHestiaIframeTarget,
  registerHestiaIframe,
  unregisterHestiaIframe,
} from './hestiaIframeRegistry'
import { isHestiaIframeRelayRouteAllowed } from './hestiaIframeRelay'
import {
  consumeHestiaPreviewOverrideFromQuery,
  getStoredHestiaPreviewOverride,
} from './hestiaPreviewOverride'

export type EmbedMode =
  | 'customers'
  | 'inbox-hermes'
  | 'orders-hermes'
  | 'payments-hermes'
  | 'phone-ordering-hermes'
  | 'agent-hermes'
  | 'launch-hermes'
  | 'loyalty'
  | 'owner-agent-widget'
  | 'website-builder-hermes'
  | 'canvas-menu-editor'
  | 'menu-review-hermes'
  | 'hardware'
  | 'print-shop'
  | 'menu-photos'

export interface HestiaParentViewport {
  height: number
  width: number
}

export interface HestiaIframeExtraData {
  phonePageVisible?: boolean
  customers?: {
    scope: 'brand'
  }
  orders?: {
    scope: 'brand'
  }
  phoneOrdering?: {
    scope: 'brand'
  }
  phoneOrderingState?: (
    | {
        scope: 'brand'
        brandId: string
        data: EndpointResponse<'hestia.v1.brands.phone.getLocations'>
      }
    | {
        scope: 'location'
        locationId: string
        data: EndpointResponse<'hestia.v1.locations.getPhoneState'>
      }
  ) & { dataUpdatedAt: number }
  printShop?: {
    checkoutHistoryAck: boolean
  }
  menu?: {
    scope: 'brand'
  }
  launch?: {
    scope: 'brand'
  }
  launchCta?: {
    href: string
    isVisible: boolean
    label: string
  }
  websiteBuilder?: {
    designMode?: 'legacy' | 'variantB'
  }
  menuId?: string
  parentViewport?: HestiaParentViewport
}

interface HestiaIframeSessionBaseData {
  apiUrl: string
  brandId?: string
  brandScopedLocationId?: string | null
  brandScopedRoutes: true
  locationId?: string
  embed?: EmbedMode
  extraData?: HestiaIframeExtraData
  isMultiBrandUser: boolean
  role?: string | null
  rootTargetPath?: string
  user: Olympus.Users.User
  targetPath?: string
}

type HestiaIframeSessionData = HestiaIframeSessionBaseData &
  (
    | {
        accessToken: string
        accessTokenAudience: string
        audience: 'hermes'
        expiresAt: string
      }
    | {
        audience: 'hermes'
        csrfToken: string
      }
  )

interface UseHestiaIframeOptions {
  brandId: Ref<string | undefined>
  embed?: EmbedMode
  extraData?: Ref<HestiaIframeExtraData | undefined>
  // Read during the session handoff; query refreshes must not remount the iframe.
  initialExtraData?: Ref<HestiaIframeExtraData | undefined>
  hestiaUrl?: string
  iframeAuthSearchParams?: Ref<Record<string, string | undefined> | undefined>
  locationId?: Ref<string | undefined>
  onMessage?: (message: unknown) => void
  rootTargetPath?: Ref<string | undefined> | string
  // The canonical route's optional location. Keep this separate from
  // locationId when iframe auth needs a fallback location to hydrate brand data.
  scopeLocationId?: Ref<string | undefined>
  // Pass a Ref when the redirect path is reactive (e.g. flag-driven); a plain
  // string when it's fixed per view. Omit to keep the embed-based default.
  targetPath?: Ref<string | undefined> | string
}

interface BuildHestiaSessionPayloadBaseOptions {
  apiUrl: string
  brandId?: string
  brandScopedLocationId?: string | null
  embed?: EmbedMode
  extraData?: HestiaIframeExtraData
  isMultiBrandUser: boolean
  locationId?: string
  role?: string | null
  rootTargetPath?: string
  targetPath?: string
  user: Olympus.Users.User
}

export type BuildHestiaSessionPayloadOptions =
  BuildHestiaSessionPayloadBaseOptions &
    (
      | {
          accessToken: string
          accessTokenAudience: string
          expiresAt: string
        }
      | {
          csrfToken: string
        }
    )

export const buildHestiaSessionPayload = (
  options: BuildHestiaSessionPayloadOptions
): HestiaIframeSessionData => {
  const {
    apiUrl,
    brandId,
    brandScopedLocationId,
    embed,
    extraData,
    isMultiBrandUser,
    locationId,
    role,
    rootTargetPath,
    targetPath,
    user,
  } = options
  const auth =
    'accessToken' in options
      ? {
          accessToken: options.accessToken,
          accessTokenAudience: options.accessTokenAudience,
          audience: 'hermes' as const,
          expiresAt: options.expiresAt,
        }
      : { audience: 'hermes' as const, csrfToken: options.csrfToken }

  return {
    ...auth,
    apiUrl,
    brandId,
    ...(brandScopedLocationId !== undefined ? { brandScopedLocationId } : {}),
    brandScopedRoutes: true,
    embed,
    extraData,
    isMultiBrandUser,
    locationId,
    ...(role !== undefined ? { role } : {}),
    ...(rootTargetPath !== undefined ? { rootTargetPath } : {}),
    user,
    // Keep the field absent (not `undefined`) when omitted — Hestia's gate is
    // `typeof targetPath === 'string'`.
    ...(targetPath !== undefined ? { targetPath } : {}),
  }
}

interface UseHestiaIframeReturn {
  error: Ref<string | null>
  iframeKey: ComputedRef<string>
  iframeRef: Ref<HTMLIFrameElement | null>
  iframeSrc: ComputedRef<string>
  isReady: Ref<boolean>
  isSessionHydrated: Ref<boolean>
  // Posts a message into the embedded iframe at its observed origin. No-ops
  // until the iframe has signalled ready (origin unknown before then).
  postToHestia: (message: object) => void
}

export const HESTIA_IFRAME_AUTH_STALE = 'HESTIA_IFRAME_AUTH_STALE'

const HESTIA_IFRAME_AUTH_STALE_ERROR = 'Unable to refresh Hestia session'
const HESTIA_UPSELL_SOURCE_TO_HERMES_SOURCE: Record<string, UpsellSourceValue> =
  {
    print_shop_gift_cards: UpsellSource.printShopGiftCards,
    website_careers: UpsellSource.websiteCareers,
    website_gift_cards: UpsellSource.websiteGiftCards,
    website_reservations: UpsellSource.websiteReservations,
  }

export const getHestiaParentViewport = (): HestiaParentViewport | undefined => {
  if (typeof window === 'undefined') {
    return undefined
  }

  return {
    height: window.innerHeight,
    width: window.innerWidth,
  }
}

export const mergeHestiaExtraDataWithParentViewport = (
  extraData: HestiaIframeExtraData | undefined,
  parentViewport: HestiaParentViewport | undefined
): HestiaIframeExtraData | undefined => {
  if (!extraData && !parentViewport) {
    return undefined
  }

  return {
    ...extraData,
    parentViewport,
  }
}

export const buildHestiaParentViewportMessage = (
  parentViewport: HestiaParentViewport
): {
  type: 'HESTIA_PARENT_VIEWPORT_UPDATED'
  viewport: HestiaParentViewport
} => ({
  type: 'HESTIA_PARENT_VIEWPORT_UPDATED',
  viewport: parentViewport,
})

export const toHestiaPostMessagePayload = <Payload extends object>(
  payload: Payload
): Payload => JSON.parse(JSON.stringify(toRaw(payload))) as Payload

export const isExpectedHestiaIframeMessage = (
  event: MessageEvent,
  expectedOrigin: string,
  iframeWindow: Window | null | undefined
): boolean => {
  return event.origin === expectedOrigin && event.source === iframeWindow
}

export const getHestiaUrl = (overrideUrl?: string): string => {
  const queryOverrideUrl = consumeHestiaPreviewOverrideFromQuery()
  const storedOverrideUrl = getStoredHestiaPreviewOverride()
  const url =
    queryOverrideUrl ??
    storedOverrideUrl ??
    overrideUrl ??
    import.meta.env.VITE_HESTIA_URL

  if (!url) {
    return 'http://localhost:8086'
  }
  return url
}

interface BuildHestiaIframeAuthUrlOptions {
  hestiaUrl: string
  searchParams?: Record<string, string | undefined>
}

export const buildHestiaIframeAuthUrl = (
  options: BuildHestiaIframeAuthUrlOptions
): string => {
  const { hestiaUrl, searchParams } = options
  const url = new URL('/iframe-auth', hestiaUrl)

  Object.entries(searchParams ?? {}).forEach(([key, value]) => {
    if (value) {
      url.searchParams.set(key, value)
    }
  })

  return url.toString()
}

export const useHestiaIframe = (
  options: UseHestiaIframeOptions
): UseHestiaIframeReturn => {
  const {
    brandId,
    embed,
    extraData,
    initialExtraData,
    hestiaUrl: providedHestiaUrl,
    iframeAuthSearchParams,
    locationId,
    onMessage,
    rootTargetPath,
    scopeLocationId,
    targetPath,
  } = options
  const authStore = useAuthStore()
  const ownerAgentStore = useOwnerAgentStore()
  const { isMultiBrandUser, role } = storeToRefs(authStore)

  const iframeRef = ref<HTMLIFrameElement | null>(null)
  const iframeReady = ref(false)
  const iframeOrigin = ref<string | null>(null)
  const sessionHydrated = ref(false)
  const sessionSent = ref(false)
  const error = ref<string | null>(null)
  const staleAuthRetryCount = ref(0)
  // Manual bump counter used by the cleanup watcher below for changes that
  // require a fresh iframe but aren't already captured in the derived key
  // (today: deep extraData updates).
  const remountCounter = ref(0)
  const embeddedOverlaySource = {}
  // Derive the Vue `:key` directly from the active brand and location so a
  // stale brand can never persist in the iframe — even if the cleanup watcher
  // below somehow missed a change, the computed key would still flip and Vue
  // would remount the iframe element (new browsing context → fresh
  // /iframe-auth handshake → new HESTIA_SESSION_HYDRATION with the active
  // brandId). This was the root cause of Daniel Park's bug where the agent
  // iframe kept reasoning about the previously selected brand after the user
  // switched brands in Hermes.
  const iframeKey = computed(
    () =>
      `${brandId.value ?? 'no-brand'}::${locationId?.value ?? 'no-location'}::scope-${scopeLocationId?.value ?? 'all'}::${remountCounter.value}`
  )
  // Compared by value, not reference: callers typically derive these from
  // `route.query`, so the object identity churns on every navigation even when
  // the values are unchanged — and each churn would remount the iframe.
  const iframeAuthSearchParamsKey = computed(() =>
    Object.entries(iframeAuthSearchParams?.value ?? {})
      .filter(([, value]) => value !== undefined)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, value]) => `${key}=${value}`)
      .join('&')
  )
  const parentViewport = ref<HestiaParentViewport | undefined>(
    getHestiaParentViewport()
  )

  const hestiaUrl = getHestiaUrl(providedHestiaUrl)
  const expectedOrigin = new URL(hestiaUrl).origin
  const shouldSendParentViewport =
    embed === 'customers' ||
    embed === 'orders-hermes' ||
    embed === 'phone-ordering-hermes'
  const hestiaIframeTarget = normalizeEmbedToHestiaIframeTarget(embed)

  const hestiaSession = computed<HestiaIframeSessionData | null>(() => {
    const iframeSession = getActiveIframeSession()
    const csrfToken = authStore.csrfToken
    const auth = iframeSession?.accessToken
      ? {
          accessToken: iframeSession.accessToken,
          accessTokenAudience: iframeSession.audience,
          expiresAt: new Date(iframeSession.expiresAt).toISOString(),
        }
      : csrfToken
        ? { csrfToken }
        : null
    if (!auth || !authStore.user) {
      return null
    }
    // Resolve Ref<string|undefined> | string to a plain string for the payload.
    const resolvedTargetPath =
      typeof targetPath === 'string' ? targetPath : targetPath?.value
    const resolvedRootTargetPath =
      typeof rootTargetPath === 'string'
        ? rootTargetPath
        : rootTargetPath?.value
    return buildHestiaSessionPayload({
      ...auth,
      apiUrl: VITE_OLYMPUS_URL,
      brandId: brandId.value,
      brandScopedLocationId: scopeLocationId
        ? (scopeLocationId.value ?? null)
        : undefined,
      embed,
      extraData: mergeHestiaExtraDataWithParentViewport(
        initialExtraData?.value
          ? { ...initialExtraData.value, ...extraData?.value }
          : extraData?.value,
        shouldSendParentViewport ? parentViewport.value : undefined
      ),
      isMultiBrandUser: isMultiBrandUser.value,
      locationId: locationId?.value,
      role: role.value,
      rootTargetPath: resolvedRootTargetPath,
      targetPath: resolvedTargetPath,
      user: toRaw(authStore.user),
    })
  })

  const sendParentViewportToHestia = () => {
    if (
      !shouldSendParentViewport ||
      !iframeRef.value?.contentWindow ||
      !iframeOrigin.value ||
      !parentViewport.value
    ) {
      return
    }

    const message = toHestiaPostMessagePayload(
      buildHestiaParentViewportMessage(parentViewport.value)
    )

    iframeRef.value.contentWindow.postMessage(message, iframeOrigin.value)
  }

  const postToHestia = (message: object): void => {
    if (!iframeRef.value?.contentWindow || !iframeOrigin.value) {
      return
    }

    iframeRef.value.contentWindow.postMessage(
      toHestiaPostMessagePayload(message),
      iframeOrigin.value
    )
  }

  const sendSessionToHestia = () => {
    if (
      sessionSent.value ||
      !iframeRef.value?.contentWindow ||
      !iframeOrigin.value ||
      !hestiaSession.value
    ) {
      return
    }

    const message = toHestiaPostMessagePayload({
      session: hestiaSession.value,
      type: 'HESTIA_SESSION_HYDRATION',
    })

    iframeRef.value.contentWindow.postMessage(message, iframeOrigin.value)

    sessionSent.value = true
  }

  const unregisterCurrentHestiaIframe = () => {
    unregisterHestiaIframe(iframeRef.value?.contentWindow)
  }

  const remountHestiaIframe = (): void => {
    ownerAgentStore.setEmbeddedHestiaOverlay(embeddedOverlaySource, false)
    unregisterCurrentHestiaIframe()
    iframeReady.value = false
    sessionSent.value = false
    sessionHydrated.value = false
    iframeOrigin.value = null
    error.value = null
    remountCounter.value++
  }

  const handleMessage = (event: MessageEvent) => {
    if (
      !isExpectedHestiaIframeMessage(
        event,
        expectedOrigin,
        iframeRef.value?.contentWindow
      )
    ) {
      return
    }

    // Reads as "if this message didn't come from MY iframe, none of my
    // business, ignore it." All Hestia iframes on the page share the same
    // origin, so filtering by origin alone isn't enough — every active
    // useHestiaIframe instance gets every Hestia iframe's messages.
    // Without the source check, the useHestiaPreload iframe firing
    // HESTIA_IFRAME_READY would flip this composable's iframeReady true and
    // trigger sendSessionToHestia against the wrong iframe before its
    // iframe-auth has had a chance to attach its message listener — the
    // session message is dropped and the widget stays stuck on the spinner.
    if (event.source !== iframeRef.value?.contentWindow) {
      return
    }

    // Reads as "if this message didn't come from MY iframe, none of my
    // business, ignore it." All Hestia iframes on the page share the same
    // origin, so filtering by origin alone isn't enough — every active
    // useHestiaIframe instance gets every Hestia iframe's messages.
    // Without the source check, the useHestiaPreload iframe firing
    // HESTIA_IFRAME_READY would flip this composable's iframeReady true and
    // trigger sendSessionToHestia against the wrong iframe before its
    // iframe-auth has had a chance to attach its message listener — the
    // session message is dropped and the widget stays stuck on the spinner.
    if (event.source !== iframeRef.value?.contentWindow) {
      return
    }

    if (event.data?.type === 'HESTIA_IFRAME_READY') {
      iframeOrigin.value = event.origin
      iframeReady.value = true

      if (hestiaIframeTarget && iframeRef.value?.contentWindow) {
        registerHestiaIframe({
          origin: event.origin,
          target: hestiaIframeTarget,
          window: iframeRef.value.contentWindow,
        })
      }
    }

    if (event.data?.type === 'HESTIA_SESSION_HYDRATION_SUCCESS') {
      staleAuthRetryCount.value = 0
      sessionHydrated.value = true
    }

    if (event.data?.type === 'HESTIA_SESSION_HYDRATION_ERROR') {
      error.value = event.data.error ?? 'Failed to hydrate session'
    }

    if (event.data?.type === HESTIA_IFRAME_AUTH_STALE) {
      if (staleAuthRetryCount.value === 0) {
        staleAuthRetryCount.value += 1
        remountHestiaIframe()
      } else {
        error.value = HESTIA_IFRAME_AUTH_STALE_ERROR
      }

      onMessage?.(event.data)
      return
    }

    if (
      embed !== 'owner-agent-widget' &&
      isHestiaModalScrimSyncMessage(event.data)
    ) {
      ownerAgentStore.setEmbeddedHestiaOverlay(
        embeddedOverlaySource,
        event.data.isOpen
      )
    }

    onMessage?.(event.data)

    if (isHestiaUpsellOpenRequestMessage(event.data)) {
      const source = HESTIA_UPSELL_SOURCE_TO_HERMES_SOURCE[event.data.source]

      if (source) {
        openUpsellSheet(source)
      }
    }

    if (isHestiaToParentRelayRequest(event.data)) {
      const source = hestiaIframeTarget
      if (
        !source ||
        !isHestiaIframeRelayRouteAllowed({
          event: event.data.event,
          source,
          target: event.data.target,
        })
      ) {
        return
      }

      postMessageToHestiaIframeTarget({
        excludeWindow: iframeRef.value?.contentWindow ?? undefined,
        message: toHestiaPostMessagePayload(
          buildParentToHestiaEvent({
            event: event.data.event,
            source,
          })
        ),
        target: event.data.target,
      })
    }
  }

  const handleResize = () => {
    parentViewport.value = getHestiaParentViewport()
    sendParentViewportToHestia()
  }

  watch(
    [
      brandId,
      () => locationId?.value,
      () => scopeLocationId?.value,
      () => extraData?.value,
      // These land in the iframe-auth URL, so a change swaps `src` and the
      // browsing context loads a fresh document. Without a remount the
      // handshake state stays behind and the new document waits forever —
      // reachable by navigating between the same route with and without an
      // intent (e.g. the legacy guest-import redirect onto Customers).
      iframeAuthSearchParamsKey,
      () =>
        typeof rootTargetPath === 'string'
          ? rootTargetPath
          : rootTargetPath?.value,
      () => (typeof targetPath === 'string' ? targetPath : targetPath?.value),
    ],
    () => {
      staleAuthRetryCount.value = 0
      // brand/location changes already flip the derived iframeKey, but
      // bumping the counter covers deep extraData updates where the brand
      // and location haven't moved.
      remountHestiaIframe()
    },
    { deep: true }
  )

  // Force a full iframe remount when the user's credentials change after
  // the iframe has already hydrated. Without this, sessionSent stays true
  // and the iframe runs against the now-invalid token until the user
  // manually refreshes — there is no listener for re-hydration once
  // iframe-auth has location.replace()d into the real route.
  //
  // Gated on sessionSent so the initial auth flow (csrfToken null → set)
  // still rides the regular send-session watch below; we only force a
  // remount when the previously-sent session has gone stale.
  watch([() => authStore.csrfToken, () => authStore.user?.id], () => {
    if (!sessionSent.value) return
    staleAuthRetryCount.value = 0
    remountHestiaIframe()
  })

  watch([iframeReady, hestiaSession, sessionSent], ([ready, sess, sent]) => {
    if (ready && sess && !sent) {
      sendSessionToHestia()
    }
  })

  onBeforeMount(() => {
    window.addEventListener('message', handleMessage)
    window.addEventListener('resize', handleResize)
  })

  onBeforeUnmount(() => {
    ownerAgentStore.setEmbeddedHestiaOverlay(embeddedOverlaySource, false)
    unregisterCurrentHestiaIframe()
    window.removeEventListener('message', handleMessage)
    window.removeEventListener('resize', handleResize)
  })

  const iframeSrc = computed(() => {
    return buildHestiaIframeAuthUrl({
      hestiaUrl,
      searchParams: iframeAuthSearchParams?.value,
    })
  })

  return {
    error,
    iframeKey,
    iframeRef,
    iframeSrc,
    isReady: iframeReady,
    isSessionHydrated: sessionHydrated,
    postToHestia,
  }
}
